import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, MessageSquare, TrendingUp, MapPin, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import { fmtUSD, FLAGS } from "@/lib/utils";
import PropertyCard from "@/components/PropertyCard";

export default function Home() {
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, d] = await Promise.all([
          api.get("/portfolio"),
          api.get("/developments?sort=newest"),
        ]);
        setPortfolio(p.data);
        setFeatured(d.data.slice(0, 3));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="max-w-7xl mx-auto">
      {/* Greeting */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-neutral-900">
            Hello <span className="serif italic text-propera-gold">{firstName}<span className="not-italic">,</span></span>
          </h1>
          <p className="text-neutral-500 mt-2 max-w-lg">Here&apos;s a snapshot of your property investment journey.</p>
        </div>
      </div>

      {/* Investment journey card */}
      <div className="mt-10 relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0E8C5A] via-[#0B734A] to-[#06432B] text-white p-8 sm:p-10">
        <div className="absolute inset-0 grain opacity-50" />
        <div className="relative">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] opacity-80 font-semibold flex items-center gap-2">
              <Sparkles size={14} /> Start Your Investment Journey
            </p>
            <h2 className="serif text-4xl sm:text-5xl mt-3 leading-[1.05]">
              Explore verified developments across <span className="italic text-propera-gold">Africa.</span>
            </h2>
            <Link
              to="/discover"
              data-testid="hero-discover-cta"
              className="inline-flex items-center gap-2 bg-white text-propera font-semibold px-5 py-2.5 rounded-full mt-6 hover:bg-neutral-100 transition"
            >
              Browse Developments <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      {/* Investments section */}
      <section className="mt-12">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Your Investments</h2>
            <p className="text-sm text-neutral-500">Track allocated units &amp; payment milestones</p>
          </div>
          <Link to="/portfolio" data-testid="home-view-portfolio" className="text-sm font-semibold text-propera hover:underline">View Portfolio →</Link>
        </div>
        {loading ? (
          <SkeletonRow />
        ) : portfolio.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            title="No Active Investments Yet"
            body="Start exploring verified property developments to allocate your first unit."
            cta={<Link to="/discover" data-testid="empty-browse" className="inline-flex items-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold text-sm">Browse Developments <ArrowRight size={14} /></Link>}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {portfolio.map((p) => (
              <div key={p.unit?.id} className="soft-card p-5">
                <p className="font-semibold text-neutral-900">{p.development?.name}</p>
                <p className="text-xs text-neutral-500 flex items-center gap-1 mt-1"><MapPin size={12} /> {p.development?.city}, {p.development?.country}</p>
                <p className="text-2xl font-bold mt-3 tabular">{fmtUSD(p.unit?.price)}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Featured */}
      <section className="mt-14">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Featured Developments</h2>
            <p className="text-sm text-neutral-500">Newest opportunities across our network</p>
          </div>
          <Link to="/discover" className="text-sm font-semibold text-propera hover:underline">See all →</Link>
        </div>
        {loading ? (
          <SkeletonRow />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((dev) => <PropertyCard key={dev.id} dev={dev} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body, cta }) {
  return (
    <div className="rounded-3xl border border-dashed border-neutral-200 bg-white p-12 text-center">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-4">
        <Icon size={22} />
      </div>
      <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
      <p className="text-sm text-neutral-500 max-w-md mx-auto mt-1">{body}</p>
      <div className="mt-5">{cta}</div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {[1, 2, 3].map((i) => (
        <div key={i} className="soft-card aspect-[4/3] bg-neutral-100 animate-pulse" />
      ))}
    </div>
  );
}
